import axios from 'axios';
import { Device } from '@twilio/voice-sdk';
import getFirebaseAuthToken from './fireBaseAuthenticationService';
import { BACKEND_URL } from '../utils/constants';

const CALL_CENTER_TIMEOUT = 30000;

// Twilio Device singleton
let twilioDevice = null;
let currentCall = null;
let currentIdentity = null;

/**
 * Initialize Twilio Device for browser-based calling
 */
export const initializeTwilioDevice = async (identity) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.post(
            `${BACKEND_URL}/call-center/token`,
            { identity },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const twilioToken = response.data.token;
        
        // Create or update the device
        if (twilioDevice) {
            twilioDevice.updateToken(twilioToken);
        } else {
            twilioDevice = new Device(twilioToken, {
                codecPreferences: ['opus', 'pcmu'],
                enableRingingState: true,
            });
            
            // Set up event handlers
            twilioDevice.on('registered', () => {
                console.log('Twilio Device registered');
            });
            
            twilioDevice.on('error', async (error) => {
                console.error('Twilio Device error:', error);
                // Auto-refresh token on expiry
                if (error.code === 20104 && currentIdentity) {
                    console.log('Token expired, refreshing...');
                    try {
                        await initializeTwilioDevice(currentIdentity);
                    } catch (refreshError) {
                        console.error('Failed to refresh token:', refreshError);
                    }
                }
            });
            
            twilioDevice.on('tokenWillExpire', async () => {
                console.log('Token will expire soon, refreshing...');
                if (currentIdentity) {
                    try {
                        const token = await getFirebaseAuthToken();
                        const response = await axios.post(
                            `${BACKEND_URL}/call-center/token`,
                            { identity: currentIdentity },
                            { headers: { Authorization: `Bearer ${token}` } }
                        );
                        twilioDevice.updateToken(response.data.token);
                        console.log('Token refreshed successfully');
                    } catch (error) {
                        console.error('Failed to refresh token:', error);
                    }
                }
            });
            
            twilioDevice.on('incoming', (call) => {
                console.log('Incoming call from:', call.parameters.From);
            });
            
            await twilioDevice.register();
            
            // Bind to system default audio devices so the SDK
            // doesn't override the OS-selected input/output (e.g. headphones).
            try {
                await twilioDevice.audio.setInputDevice('default');
                twilioDevice.audio.speakerDevices.set('default');
                twilioDevice.audio.ringtoneDevices.set('default');
            } catch (audioErr) {
                console.warn('Could not set default audio devices:', audioErr);
            }
            
            // Re-sync when the user changes system audio settings
            if (navigator.mediaDevices?.addEventListener) {
                navigator.mediaDevices.addEventListener('devicechange', async () => {
                    if (!twilioDevice) return;
                    try {
                        await twilioDevice.audio.setInputDevice('default');
                        twilioDevice.audio.speakerDevices.set('default');
                        twilioDevice.audio.ringtoneDevices.set('default');
                        console.log('Audio devices re-synced after system change');
                    } catch (e) {
                        console.warn('Failed to re-sync audio devices:', e);
                    }
                });
            }
        }
        
        currentIdentity = identity;
        return twilioDevice;
    } catch (error) {
        console.error('Failed to initialize Twilio Device:', error);
        throw error;
    }
};

/**
 * Make an outbound call using the browser
 */
export const makeCall = async (toPhone, onStatusChange) => {
    if (!twilioDevice) {
        throw new Error('Twilio Device not initialized. Call initializeTwilioDevice first.');
    }
    
    try {
        currentCall = await twilioDevice.connect({
            params: { To: toPhone }
        });
        
        currentCall.on('accept', () => {
            onStatusChange?.('connected');
        });
        
        currentCall.on('disconnect', () => {
            onStatusChange?.('disconnected');
            currentCall = null;
        });
        
        currentCall.on('cancel', () => {
            onStatusChange?.('cancelled');
            currentCall = null;
        });
        
        currentCall.on('reject', () => {
            onStatusChange?.('rejected');
            currentCall = null;
        });
        
        currentCall.on('ringing', () => {
            onStatusChange?.('ringing');
        });
        
        return currentCall;
    } catch (error) {
        console.error('Failed to make call:', error);
        throw error;
    }
};

/**
 * Hang up the current call
 */
export const hangupCall = () => {
    if (currentCall) {
        currentCall.disconnect();
        currentCall = null;
    }
};

/**
 * Get the current call state
 */
export const getCurrentCall = () => currentCall;

/**
 * Check if Twilio Device is ready
 */
export const isTwilioReady = () => twilioDevice?.state === 'registered';

/**
 * Communication chain status constants
 */
export const COMMUNICATION_CHAIN_STATUS = {
    OPEN: 'open',
    PENDING_RESPONSE: 'pending_response',
    RESOLVED: 'resolved',
    ARCHIVED: 'archived',
};

/**
 * Communication event type constants
 */
export const COMMUNICATION_EVENT_TYPES = {
    INBOUND_CALL: 'inbound_call',
    OUTBOUND_CALL: 'outbound_call',
    VOICEMAIL_LEFT: 'voicemail_left',
    VOICEMAIL_RECEIVED: 'voicemail_received',
    TEXT_SENT: 'text_sent',
    TEXT_RECEIVED: 'text_received',
    EMAIL_SENT: 'email_sent',
    EMAIL_RECEIVED: 'email_received',
    FACEBOOK_MESSAGE_SENT: 'facebook_message_sent',
    FACEBOOK_MESSAGE_RECEIVED: 'facebook_message_received',
    INSTAGRAM_MESSAGE_SENT: 'instagram_message_sent',
    INSTAGRAM_MESSAGE_RECEIVED: 'instagram_message_received',
    REMINDER_TEXT: 'reminder_text',
    REMINDER_VOICEMAIL: 'reminder_voicemail',
    REMINDER_SPOKE_TO: 'reminder_spoke_to',
    REMINDER_CONFIRMED: 'reminder_confirmed',
    REMINDER_DECLINED_CANCELLED: 'reminder_declined_cancelled',
    REMINDER_DECLINED_RESCHEDULE_REQUESTED: 'reminder_declined_reschedule_requested',
    REMINDER_DECLINED_RESCHEDULED: 'reminder_declined_rescheduled',
    BOOKED: 'booked',
    APPOINTMENT_CANCELLED: 'appointment_cancelled',
    OTHER: 'other',
};

/**
 * Chain type constants
 */
export const COMMUNICATION_CHAIN_TYPES = {
    GENERAL: 'general',
    REMINDER: 'reminder',
};

/**
 * Cancellation fee status constants
 * Used when client cancels within 24 hours of appointment (50% fee policy)
 */
export const CANCELLATION_FEE_STATUS = {
    NOT_APPLICABLE: 'not_applicable',  // Cancelled with >24hr notice
    UNPAID: 'cancellation_fee_unpaid',
    PAID: 'cancellation_fee_paid',
    WAIVED: 'cancellation_fee_waived',  // At salon's discretion
};

/**
 * Fetch groombot call logs from BigQuery
 * 
 * @param {Object} options - Query options
 * @param {number} options.locationId - Filter by location
 * @param {string} options.startDate - ISO date string
 * @param {string} options.endDate - ISO date string
 * @param {number} options.limit - Max results
 * @returns {Promise<{calls: Array, count: number}>}
 */
export const fetchGroombotCalls = async ({ locationId, startDate, endDate, limit = 50 } = {}) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = { limit };
        if (locationId) payload.location_id = locationId;
        if (startDate) payload.start_date = startDate;
        if (endDate) payload.end_date = endDate;
        
        const response = await axios.post(
            `${BACKEND_URL}/call-center/groombot-calls`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error fetching groombot calls:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Fetch appointments booked by the ZoomBot (ReceptionBot)
 * 
 * @param {Object} options - Query options
 * @param {number} options.locationId - Filter by location
 * @param {number} options.limit - Max results
 * @returns {Promise<{appointments: Array, count: number}>}
 */
export const fetchZoomBotAppointments = async ({ locationId, limit = 100 } = {}) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const params = new URLSearchParams();
        if (locationId) params.append('location_id', locationId);
        params.append('limit', limit);
        
        const response = await axios.get(
            `${BACKEND_URL}/call-center/zoombot-appointments?${params.toString()}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error fetching ZoomBot appointments:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Fetch communication chains (client communication tracking)
 * 
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (single value or comma-separated)
 * @param {number} options.locationId - Filter by location
 * @param {string} options.chainType - Firestore equality filter (general, reminder, email)
 * @param {string} options.onlyChainType - In-memory equality filter (e.g. email)
 * @param {string} options.excludeChainType - In-memory inequality filter (e.g. email)
 * @param {string} options.appointmentDate - Filter reminder chains by date (YYYY-MM-DD)
 * @param {number} options.clientId - Filter by DaySmart client ID
 * @param {string} options.clientPhone - Filter by client phone
 * @param {string} options.search - Server-side search across name, email, phone, notes
 * @param {number} options.limit - Page size
 * @param {number} options.offset - Number of matching chains to skip
 * @returns {Promise<{chains: Array, count: number, total: number, has_more: boolean}>}
 */
export const fetchCommunicationChains = async ({
    status,
    locationId,
    chainType,
    onlyChainType,
    excludeChainType,
    appointmentDate,
    clientId,
    clientPhone,
    search,
    limit = 50,
    offset = 0,
} = {}) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (locationId) params.append('location_id', locationId);
        if (chainType) params.append('chain_type', chainType);
        if (onlyChainType) params.append('only_chain_type', onlyChainType);
        if (excludeChainType) params.append('exclude_chain_type', excludeChainType);
        if (appointmentDate) params.append('appointment_date', appointmentDate);
        if (clientId) params.append('client_id', clientId);
        if (clientPhone) params.append('client_phone', clientPhone);
        if (search) params.append('search', search);
        params.append('limit', String(limit));
        if (offset) params.append('offset', String(offset));
        
        const response = await axios.get(
            `${BACKEND_URL}/call-center/communication-chains?${params.toString()}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error fetching communication chains:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Create a new communication chain
 * 
 * @param {Object} data - Communication chain data
 * @param {number} data.clientId - DaySmart client ID (optional)
 * @param {string} data.clientName - Client name
 * @param {string} data.clientPhone - Client phone
 * @param {number} data.locationId - Location ID
 * @param {string} data.chainType - Chain type: "general" or "reminder" (default: "general")
 * @param {number} data.appointmentId - For reminder chains: the appointment ID
 * @param {string} data.appointmentDate - For reminder chains: the appointment date (YYYY-MM-DD)
 * @param {string} data.notes - Initial notes
 * @param {Object} data.initialEvent - First event
 * @returns {Promise<{success: boolean, chain_id: string, chain: Object}>}
 */
export const createCommunicationChain = async ({ clientId, clientName, clientPhone, locationId, chainType, appointmentId, appointmentDate, notes, initialEvent }) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = {
            client_name: clientName,
            client_phone: clientPhone,
            location_id: locationId,
        };
        if (clientId) payload.client_id = clientId;
        if (chainType) payload.chain_type = chainType;
        if (appointmentId) payload.appointment_id = appointmentId;
        if (appointmentDate) payload.appointment_date = appointmentDate;
        if (notes) payload.notes = notes;
        if (initialEvent) payload.initial_event = initialEvent;
        
        const response = await axios.post(
            `${BACKEND_URL}/call-center/communication-chains`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error creating communication chain:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Add an event to a communication chain
 * 
 * @param {string} chainId - Communication chain ID
 * @param {Object} event - Event data
 * @param {string} event.type - Event type
 * @param {string} event.notes - Event notes
 * @param {number} event.userId - User ID
 * @returns {Promise<{success: boolean, chain_id: string, event: Object, status: string}>}
 */
export const addCommunicationChainEvent = async (chainId, { type, notes, userId }) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = { type };
        if (notes) payload.notes = notes;
        if (userId) payload.user_id = userId;
        
        const response = await axios.post(
            `${BACKEND_URL}/call-center/communication-chains/${chainId}/events`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error adding communication chain event:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Record a communication event, adding to an existing open chain or creating a new one.
 * This is the preferred method for recording outbound calls, texts, emails, etc.
 * 
 * @param {Object} data - Event data
 * @param {number} data.clientId - DaySmart client ID (optional)
 * @param {string} data.clientPhone - Client phone number (required)
 * @param {string} data.clientName - Client name (required)
 * @param {number} data.locationId - Location ID (required)
 * @param {string} data.eventType - Event type (required)
 * @param {string} data.eventNotes - Event notes (optional)
 * @param {number} data.userId - User who initiated (optional)
 * @param {number} data.appointmentId - For reminder-related events (optional)
 * @param {string} data.appointmentDate - For reminder-related events YYYY-MM-DD (optional)
 * @returns {Promise<{success: boolean, chain_id: string, action: string, event: Object, status: string}>}
 */
export const recordCommunicationEvent = async ({ clientId, clientPhone, clientName, locationId, eventType, eventNotes, userId, callSid, appointmentId, appointmentDate }) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = {
            client_phone: clientPhone,
            client_name: clientName,
            location_id: locationId,
            event_type: eventType,
        };
        if (clientId) payload.client_id = clientId;
        if (eventNotes) payload.event_notes = eventNotes;
        if (userId) payload.user_id = userId;
        if (callSid) payload.call_sid = callSid;
        if (appointmentId) payload.appointment_id = appointmentId;
        if (appointmentDate) payload.appointment_date = appointmentDate;
        
        const response = await axios.post(
            `${BACKEND_URL}/call-center/record-communication-event`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error recording communication event:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Update a communication chain's status
 * 
 * @param {string} chainId - Communication chain ID
 * @param {string} status - New status (open, pending_callback, resolved, complete)
 * @param {string} notes - Optional notes
 * @returns {Promise<{success: boolean, chain_id: string, status: string}>}
 */
export const updateCommunicationChainStatus = async (chainId, status, notes) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = { status };
        if (notes) payload.notes = notes;
        
        const response = await axios.put(
            `${BACKEND_URL}/call-center/communication-chains/${chainId}/status`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error updating communication chain status:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Mark a communication chain as resolved
 * 
 * @param {string} chainId - Communication chain ID
 * @param {string} notes - Optional completion notes
 * @returns {Promise<{success: boolean, chain_id: string, status: string}>}
 */
export const markCommunicationChainResolved = async (chainId, notes) => {
    return updateCommunicationChainStatus(chainId, COMMUNICATION_CHAIN_STATUS.RESOLVED, notes);
};

/**
 * Link, unlink, or dismiss the DaySmart client on a communication chain.
 *
 * @param {string} chainId
 * @param {Object} data
 * @param {"link"|"unlink"|"dismiss_suggestion"} data.action
 * @param {number} [data.clientId]
 * @param {string} [data.clientName]
 * @param {string[]} [data.petNames]
 * @param {string} [data.clientPhone] Omit on link to keep the chain's current phone.
 * @param {number} [data.userId]
 */
export const updateCommunicationChainClient = async (chainId, {
    action,
    clientId,
    clientName,
    petNames,
    clientPhone,
    userId,
}) => {
    try {
        const token = await getFirebaseAuthToken();
        const payload = { action };
        if (clientId) payload.client_id = clientId;
        if (clientName) payload.client_name = clientName;
        if (petNames) payload.pet_names = petNames;
        if (clientPhone !== undefined) payload.client_phone = clientPhone;
        if (userId) payload.user_id = userId;

        const response = await axios.put(
            `${BACKEND_URL}/call-center/communication-chains/${chainId}/client`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error updating communication chain client:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Delete a communication chain
 * 
 * @param {string} chainId - Communication chain ID
 * @returns {Promise<{success: boolean, chain_id: string}>}
 */
export const deleteCommunicationChain = async (chainId) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.delete(
            `${BACKEND_URL}/call-center/communication-chains/${chainId}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error deleting communication chain:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Bulk update communication chains (resolve or archive)
 * 
 * @param {string[]} chainIds - Array of chain IDs
 * @param {string} action - "archive" or "resolve"
 * @returns {Promise<{success: boolean, action: string, succeeded: number, failed: number}>}
 */
export const bulkUpdateCommunicationChains = async (chainIds, action) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.post(
            `${BACKEND_URL}/call-center/communication-chains/bulk`,
            { chain_ids: chainIds, action },
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        return response.data;
    } catch (error) {
        console.error("Error in bulk chain operation:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Send an email reply to a client via the communication chain.
 * Uses the salon's Gmail credentials (domain-wide delegation) to send.
 * 
 * @param {string} chainId - Communication chain ID
 * @param {Object} data - Email data
 * @param {string} data.toEmail - Recipient email address
 * @param {string} data.subject - Email subject line
 * @param {string} data.body - Plain text email body
 * @param {string} data.inReplyTo - Optional Message-ID for threading
 * @param {number} data.userId - User sending the email
 * @returns {Promise<{success: boolean, chain_id: string, gmail_id: string, thread_id: string}>}
 */
export const sendEmailReply = async (chainId, { toEmail, subject, body, inReplyTo, userId }) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const payload = {
            to_email: toEmail,
            subject,
            body,
        };
        if (inReplyTo) payload.in_reply_to = inReplyTo;
        if (userId) payload.user_id = userId;
        
        const response = await axios.post(
            `${BACKEND_URL}/call-center/communication-chains/${chainId}/reply-email`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error sending email reply:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Request an AI-suggested email reply for a communication chain.
 *
 * @param {string} chainId - Communication chain ID
 * @param {Array} upcomingAppointments - Optional upcoming appointments for context
 * @returns {Promise<{suggestion: string}>}
 */
export const suggestEmailReply = async (chainId, upcomingAppointments = []) => {
    try {
        const token = await getFirebaseAuthToken();

        const payload = {};
        if (upcomingAppointments.length > 0) {
            payload.upcoming_appointments = upcomingAppointments;
        }

        const response = await axios.post(
            `${BACKEND_URL}/call-center/communication-chains/${chainId}/suggest-reply`,
            payload,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 60000, // AI can take a moment
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error getting AI suggestion:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Initiate an outbound call to a client
 * 
 * @param {string} toPhone - Destination phone number
 * @param {number} locationId - Location for caller ID
 * @returns {Promise<{success: boolean, call_sid: string, to: string, from: string, status: string}>}
 */
export const initiateCall = async (toPhone, locationId) => {
    try {
        const token = await getFirebaseAuthToken();
        
        const response = await axios.post(
            `${BACKEND_URL}/call-center/initiate-call`,
            {
                to_phone: toPhone,
                location_id: locationId,
            },
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        
        return response.data;
    } catch (error) {
        console.error("Error initiating call:", error);
        throw new Error(error?.response?.data?.error || error.message);
    }
};

/**
 * Format phone number for display
 */
export const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === '1') {
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
};

/**
 * Format duration in seconds to human readable
 */
export const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get status badge color
 */
export const getStatusColor = (status) => {
    switch (status) {
        case COMMUNICATION_CHAIN_STATUS.OPEN:
            return 'bg-yellow-100 text-yellow-800';
        case COMMUNICATION_CHAIN_STATUS.PENDING_RESPONSE:
            return 'bg-blue-100 text-blue-800';
        case COMMUNICATION_CHAIN_STATUS.RESOLVED:
            return 'bg-green-100 text-green-800';
        case COMMUNICATION_CHAIN_STATUS.ARCHIVED:
            return 'bg-gray-100 text-gray-500';
        default:
            return 'bg-gray-100 text-gray-800';
    }
};

/**
 * Get event type display info
 */
export const getEventTypeInfo = (type) => {
    const types = {
        [COMMUNICATION_EVENT_TYPES.INBOUND_CALL]: { label: 'Inbound Call', icon: '📞', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.OUTBOUND_CALL]: { label: 'Outbound Call', icon: '📱', color: 'text-blue-600' },
        [COMMUNICATION_EVENT_TYPES.VOICEMAIL_LEFT]: { label: 'Left Voicemail', icon: '📤', color: 'text-purple-600' },
        [COMMUNICATION_EVENT_TYPES.VOICEMAIL_RECEIVED]: { label: 'Received Voicemail', icon: '📥', color: 'text-purple-600' },
        [COMMUNICATION_EVENT_TYPES.TEXT_SENT]: { label: 'Text Sent', icon: '💬', color: 'text-blue-600' },
        [COMMUNICATION_EVENT_TYPES.TEXT_RECEIVED]: { label: 'Text Received', icon: '📩', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.EMAIL_SENT]: { label: 'Email Sent', icon: '📧', color: 'text-blue-600' },
        [COMMUNICATION_EVENT_TYPES.EMAIL_RECEIVED]: { label: 'Email Received', icon: '📨', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.FACEBOOK_MESSAGE_SENT]: { label: 'Facebook Sent', icon: '📘', color: 'text-blue-600' },
        [COMMUNICATION_EVENT_TYPES.FACEBOOK_MESSAGE_RECEIVED]: { label: 'Facebook Received', icon: '📘', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.INSTAGRAM_MESSAGE_SENT]: { label: 'Instagram Sent', icon: '📸', color: 'text-pink-600' },
        [COMMUNICATION_EVENT_TYPES.INSTAGRAM_MESSAGE_RECEIVED]: { label: 'Instagram Received', icon: '📸', color: 'text-pink-600' },
        [COMMUNICATION_EVENT_TYPES.REMINDER_TEXT]: { label: 'Reminder Text', icon: '📲', color: 'text-blue-600' },
        [COMMUNICATION_EVENT_TYPES.REMINDER_VOICEMAIL]: { label: 'Reminder Voicemail', icon: '🎤', color: 'text-purple-600' },
        [COMMUNICATION_EVENT_TYPES.REMINDER_SPOKE_TO]: { label: 'Spoke To Client', icon: '🗣️', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.REMINDER_CONFIRMED]: { label: 'Confirmed', icon: '✅', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.REMINDER_DECLINED_CANCELLED]: { label: 'Cancelled', icon: '❌', color: 'text-red-600' },
        [COMMUNICATION_EVENT_TYPES.REMINDER_DECLINED_RESCHEDULE_REQUESTED]: { label: 'Reschedule Requested', icon: '📅', color: 'text-orange-600' },
        [COMMUNICATION_EVENT_TYPES.REMINDER_DECLINED_RESCHEDULED]: { label: 'Rescheduled', icon: '🔄', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.BOOKED]: { label: 'Appointment Booked', icon: '📅', color: 'text-green-600' },
        [COMMUNICATION_EVENT_TYPES.APPOINTMENT_CANCELLED]: { label: 'Appointment Cancelled', icon: '🚫', color: 'text-red-600' },
        [COMMUNICATION_EVENT_TYPES.OTHER]: { label: 'Note', icon: '📝', color: 'text-gray-600' },
    };
    return types[type] || types[COMMUNICATION_EVENT_TYPES.OTHER];
};

/**
 * ReceptionBot/GroomBot URL for reminder calls
 */
const GROOMBOT_URL = process.env.REACT_APP_GROOMBOT_URL || 'https://receptionbot-service-url';

/**
 * Trigger GroomBot to make a reminder call for an appointment
 * 
 * @param {Object} appointment - Appointment details
 * @param {string} appointment.client_phone - Phone number to call
 * @param {number} appointment.appointment_id - DaySmart appointment ID
 * @param {string} appointment.appointment_date - Date of appointment (YYYY-MM-DD)
 * @param {string} appointment.appointment_time - Time of appointment (e.g., "10:00 AM")
 * @param {number} appointment.location_id - Location ID (1=Hopkins, 2=Inver Grove)
 * @param {string} appointment.client_name - Client name
 * @param {string} appointment.pet_name - Pet name
 * @returns {Promise<{success: boolean, call_sid?: string, chain_id?: string, error?: string}>}
 */
export const triggerGroombotReminderCall = async (appointment) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.post(
            `${GROOMBOT_URL}/voice/outbound-reminder`,
            {
                to_phone: appointment.client_phone,
                appointment_id: appointment.appointment_id,
                appointment_date: appointment.appointment_date,
                appointment_time: appointment.appointment_time,
                location_id: appointment.location_id,
                client_name: appointment.client_name,
                client_id: appointment.client_id,
                pet_name: appointment.pet_name,
            },
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error triggering GroomBot reminder call:', error);
        throw new Error(error.response?.data?.error || error.message);
    }
};

/**
 * Trigger GroomBot reminder calls for multiple appointments
 * Calls are initiated sequentially to avoid overwhelming the system
 * 
 * @param {Array} appointments - Array of appointment objects
 * @param {Function} onProgress - Callback for progress updates (called with { completed, total, current, result })
 * @returns {Promise<{successful: number, failed: number, results: Array}>}
 */
export const triggerBatchGroombotReminderCalls = async (appointments, onProgress) => {
    const results = [];
    let successful = 0;
    let failed = 0;

    for (let i = 0; i < appointments.length; i++) {
        const appt = appointments[i];
        try {
            const result = await triggerGroombotReminderCall(appt);
            results.push({ appointment: appt, success: true, ...result });
            successful++;
            // Note: The ReceptionBot backend creates/updates the communication chain
            // and adds an outbound_call event when /voice/outbound-reminder is called,
            // then adds a completion event with transcript + audio when the call ends.
        } catch (error) {
            results.push({ appointment: appt, success: false, error: error.message });
            failed++;
        }

        if (onProgress) {
            onProgress({
                completed: i + 1,
                total: appointments.length,
                current: appt,
                result: results[results.length - 1],
            });
        }

        // Small delay between calls to avoid rate limiting
        if (i < appointments.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    }

    return { successful, failed, results };
};

/**
 * Fetch communication chains for a specific client
 * 
 * @param {number} clientId - DaySmart client ID
 * @param {number} locationId - Location ID
 * @param {string} clientPhone - Client phone number (used as fallback for chains created without client_id)
 * @param {number} limit - Max results
 * @returns {Promise<{chains: Array}>}
 */
export const fetchClientCommunicationChains = async (clientId, locationId, clientPhone, limit = 20) => {
    try {
        const token = await getFirebaseAuthToken();
        const params = new URLSearchParams({
            client_id: clientId,
            location_id: locationId,
            limit: limit.toString(),
        });
        if (clientPhone) {
            params.append('client_phone', clientPhone.replace(/\D/g, ''));
        }

        const response = await axios.get(
            `${BACKEND_URL}/call-center/communication-chains?${params}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching client communication chains:', error);
        throw new Error(error.response?.data?.error || error.message);
    }
};

export const fetchReminderChains = async (appointmentDate, locationId = null) => {
    try {
        const token = await getFirebaseAuthToken();
        const params = new URLSearchParams({
            chain_type: 'reminder',
            appointment_date: appointmentDate,
        });
        if (locationId) {
            params.append('location_id', locationId);
        }

        const response = await axios.get(
            `${BACKEND_URL}/call-center/communication-chains?${params}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching reminder chains:', error);
        throw new Error(error.response?.data?.error || error.message);
    }
};

/**
 * Call the quote-builder service to get grooming price estimates.
 * Supports two paths:
 *   - Existing client: provide client_id + dog_names
 *   - New client: provide client_message (voicemail transcript / email body)
 *
 * @param {Object} params
 * @param {number} [params.client_id] - DaySmart client ID
 * @param {string[]} [params.dog_names] - Pet names to quote for
 * @param {string} [params.client_message] - Voicemail transcript or email body
 * @param {number} [params.location_id] - Location (1=Hopkins, 2=Inver Grove)
 * @returns {Promise<Object>} Quote result with pet_quotes array
 */
export const quoteServices = async ({ client_id, dog_names, client_message, location_id }) => {
    try {
        const token = await getFirebaseAuthToken();
        const response = await axios.post(
            `${BACKEND_URL}/quote-builder`,
            { client_id, dog_names, client_message, location_id },
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 60000,
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error calling quote builder:', error);
        throw new Error(error.response?.data?.error || error.message);
    }
};

/**
 * Get a signed URL for a voicemail audio file stored in GCS.
 * @param {string} blobName - GCS blob name from the event's audio_blob_name field
 * @returns {Promise<string>} - Signed URL for audio playback (valid 15 min)
 */
export const getVoicemailAudioUrl = async (blobName, bucket) => {
    try {
        const token = await getFirebaseAuthToken();
        const params = new URLSearchParams({ blob_name: blobName });
        if (bucket) {
            params.append('bucket', bucket);
        }
        
        const response = await axios.get(
            `${BACKEND_URL}/call-center/voicemail-audio?${params.toString()}`,
            {
                headers: { Authorization: `Bearer ${token}` },
                timeout: CALL_CENTER_TIMEOUT,
            }
        );
        return response.data.signed_url;
    } catch (error) {
        console.error('Error fetching voicemail audio URL:', error);
        throw new Error(error.response?.data?.error || error.message);
    }
};
